import { Button } from "@blueskydataplatform/shared-ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@blueskydataplatform/shared-ui/components/ui/card";
import { Checkbox } from "@blueskydataplatform/shared-ui/components/ui/checkbox";
import { Input } from "@blueskydataplatform/shared-ui/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@blueskydataplatform/shared-ui/components/ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@blueskydataplatform/shared-ui/components/ui/form";
import { SignUpConsentCheckboxLabel } from "@blueskydataplatform/shared-ui/components/sign-up-fields/sign-up-consent-checkbox-label";
import { marketingConsentFromCheckbox } from "@blueskydataplatform/shared-ui/utils/sign-up-consent-checkboxes";
import { getIsBadgeSolid, getUiBranding } from "@blueskydataplatform/shared-ui/theme/ui-branding";
import type { NormalizedSignUpConsentCheckboxesConfig } from "@blueskydataplatform/shared-ui/types/platform.types";
import { useToast } from "@blueskydataplatform/shared-ui/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle } from "lucide-react";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";

import Logo from "@/components/logo";
import { UILink } from "@/components/ui-link";
import { paths } from "@/config/paths";
import { GENERIC_CLIENT, useMcpClientName } from "@/features/login/mcp-client-name";
import { usePreserveQuery } from "@/hooks/use-preserve-query";
import useQueryParams, { SIGN_IN_QUERY } from "@/hooks/use-query-params";
import { getSafeRedirect } from "@/lib/utils";
import { useMcpProvider } from "@/providers/mcp-providers-context";
import { useSignUp, type SignUpInput } from "../api/sign-up";

const schema = z.object({
  email: z.string().min(1, "Email is required").email("Enter a valid email address"),
  termsAccepted: z.boolean(),
  subscribeToMarketingEmails: z.boolean().optional(),
});

type FormValues = z.infer<typeof schema>;

/**
 * Sign-up for visitors who arrived through an MCP client. Two interactions, because they
 * came to connect a data source from a chat window, not to fill in a registration form.
 * Everything else — name, use case, company — is asked for later in the console, and
 * required at checkout. The password is generated server-side and reaches the user as a
 * set-password link in the welcome email.
 *
 * Deliberately a separate component: `platform-sign-up-form.tsx` is 828 lines and its
 * schema already forks on `inviteCode`; a third shape would make it three interleaved forms.
 */
const McpSignUpForm = ({ consentCheckboxes }: { consentCheckboxes: NormalizedSignUpConsentCheckboxesConfig }) => {
  const { email, state: mcpState, callbackUrl } = useQueryParams();
  const { provider } = useMcpProvider();
  // The LLM client they came from ("Claude", "ChatGPT"), not the data provider they are connecting to.
  const clientName = useMcpClientName(mcpState, callbackUrl);
  const { toast } = useToast();
  const navigate = useNavigate();
  const preserveQuery = usePreserveQuery();
  const uiBranding = getUiBranding();
  const isSolid = getIsBadgeSolid(uiBranding);
  const consentBranding = useMemo(
    () => ({
      companyName: uiBranding.companyName,
      termsUrl: uiBranding.links?.terms,
      privacyUrl: uiBranding.links?.privacy,
    }),
    [uiBranding.companyName, uiBranding.links?.privacy, uiBranding.links?.terms]
  );

  const terms = consentCheckboxes.termsOfService;
  const marketing = consentCheckboxes.marketingEmails;
  const formSchema = useMemo(
    () =>
      schema.superRefine((data, ctx) => {
        if (terms.enabled && terms.required && !data.termsAccepted) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["termsAccepted"],
            message: terms.requiredMessage ?? "You must accept the Terms of Service",
          });
        }
        if (
          marketing.enabled &&
          marketing.required &&
          !marketingConsentFromCheckbox(Boolean(data.subscribeToMarketingEmails), marketing.optOut)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["subscribeToMarketingEmails"],
            message: marketing.requiredMessage ?? "This field is required",
          });
        }
      }),
    [
      terms.enabled,
      terms.required,
      terms.requiredMessage,
      marketing.enabled,
      marketing.required,
      marketing.requiredMessage,
      marketing.optOut,
    ]
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    mode: "onTouched",
    defaultValues: { email: email || "", termsAccepted: false, subscribeToMarketingEmails: marketing.optOut },
  });

  const registerMutation = useSignUp({
    mutationConfig: {
      onSuccess: response => {
        const redirect = getSafeRedirect(response.data.redirect);
        if (redirect) {
          window.location.href = redirect;
          return;
        }
        navigate(preserveQuery(paths.finishRegistration.getHref()));
      },
      onError: (err: any) => {
        const error = err.response?.data ?? {};

        // A returning user connecting a second client: send them to sign in with the MCP
        // query intact, so the callback completes instead of dead-ending on a field error.
        if (error.field === "email" && String(error.code ?? "").includes("EXISTS")) {
          toast.info("You already have an account — sign in to finish connecting.");
          navigate(preserveQuery(paths.home.getHref(), SIGN_IN_QUERY));
          return;
        }

        if (error.code === "MCP_STATE_REQUIRED") {
          form.setError("root", {
            type: "manual",
            message: `This connection has expired. Start the connection again from ${clientName ?? GENERIC_CLIENT}.`,
          });
          return;
        }

        const message =
          error.error || error.message || "An error occurred while creating your account. Please try again.";
        form.setError("root", { type: "manual", message });
        toast.error(message);
      },
    },
  });

  const onSubmit = (values: FormValues) => {
    // No password: the server recognises the lean shape by its absence and generates one.
    const payload: SignUpInput = {
      email: values.email,
      hasCheckedVal: false, // honeypot field
      isFromMCP: true,
      provider: provider?.id,
      state: mcpState,
      subscribeToMarketingEmails: marketing.enabled ? values.subscribeToMarketingEmails : false,
    };
    registerMutation.mutate({ data: payload });
  };

  const rootErrorMessage = form.formState.errors.root?.message;

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-xl">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-2">
            <Logo className="mb-4" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">
            Connect {clientName ?? GENERIC_CLIENT} to {uiBranding.productName}
          </CardTitle>
          <CardDescription className="text-center">
            Enter your email to finish connecting. You can complete your profile later.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email *</FormLabel>
                    <FormControl>
                      <Input type="email" autoFocus placeholder="Enter email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {terms.enabled ? (
                <FormField
                  control={form.control}
                  name="termsAccepted"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-start space-x-3">
                        <FormControl>
                          <Checkbox checked={Boolean(field.value)} onCheckedChange={v => field.onChange(Boolean(v))} />
                        </FormControl>
                        <FormLabel className="font-normal">
                          <SignUpConsentCheckboxLabel
                            config={terms}
                            branding={consentBranding}
                            fallback={
                              <>
                                I agree to the {uiBranding.companyName}{" "}
                                <a
                                  href={uiBranding.links?.terms}
                                  target="_blank"
                                  className="underline"
                                  rel="noreferrer"
                                >
                                  Terms of Service
                                </a>
                              </>
                            }
                          />
                        </FormLabel>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}

              {marketing.enabled ? (
                <FormField
                  control={form.control}
                  name="subscribeToMarketingEmails"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-start space-x-3">
                        <FormControl>
                          <Checkbox
                            checked={marketingConsentFromCheckbox(Boolean(field.value), marketing.optOut)}
                            onCheckedChange={v =>
                              field.onChange(marketingConsentFromCheckbox(Boolean(v), marketing.optOut))
                            }
                          />
                        </FormControl>
                        <FormLabel className="font-normal">
                          <SignUpConsentCheckboxLabel
                            config={marketing}
                            branding={consentBranding}
                            fallback={
                              <>
                                I would like to receive emails from {uiBranding.companyName} on products, services, news
                                and events.
                              </>
                            }
                          />
                        </FormLabel>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}

              {rootErrorMessage ? (
                <Alert variant="destructive" solid={isSolid}>
                  <AlertCircle />
                  <AlertTitle>Unable to connect</AlertTitle>
                  <AlertDescription>{rootErrorMessage}</AlertDescription>
                </Alert>
              ) : null}

              <div className="space-y-4">
                <Button type="submit" className="w-full mt-4" disabled={registerMutation.isPending}>
                  {registerMutation.isPending ? "Connecting..." : "Continue"}
                </Button>
                <p className="text-center text-xs text-muted-foreground">
                  We&apos;ll email you a link to set your password.
                </p>
                <div className="text-center text-sm">
                  <UILink to={preserveQuery(paths.home.getHref(), SIGN_IN_QUERY)} className="text-sm">
                    Already have an account? Sign in
                  </UILink>
                </div>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default McpSignUpForm;
